import { Component } from '@theme/component';

/** localStorage key holding { status: 'dismissed' | 'subscribed', at: ms }. Visitors from before the tab
 * was removed may still hold 'teaser', which counts as dismissed. */
const STORAGE_KEY = 'ari_rr_state';
const KLAVIYO_REVISION = '2024-10-15';
const SOURCE = 'Ride Report pop-up';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** How often to look again when something else is on screen at the moment the pop-up wants to open. */
const RETRY_MS = 2000;

/**
 * "The Ride Report" email pop-up (snippets/ari-ride-report-popup.liquid).
 *
 * The first visit opens it after data-delay ms or data-scroll % of the page, whichever comes first.
 * Closing it without subscribing hides it for data-dismiss-days, and visitors who subscribe never
 * see it again. With data-manual
 * (cart and account pages) it only opens when asked, e.g. on:click="#AriRideReport/open".
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The pop-up.
 * @property {HTMLFormElement} form - The sign-up form.
 * @property {HTMLInputElement} email - Email field.
 * @property {HTMLElement} error - Field error message.
 * @property {HTMLButtonElement} submit - Subscribe button.
 * @property {HTMLElement} submitLabel - Subscribe button text.
 * @property {HTMLElement} formStep - Copy, perks and form.
 * @property {HTMLElement} successStep - "You're subscribed".
 * @property {HTMLElement} successTitle - Success heading, focused after subscribing.
 * @property {HTMLElement} live - Screen reader announcements.
 *
 * @extends {Component<Refs>}
 */
class AriRideReport extends Component {
  requiredRefs = ['dialog', 'form', 'email', 'error', 'submit', 'submitLabel', 'formStep', 'successStep'];

  /** @type {HTMLElement | null} */
  #returnFocus = null;
  #busy = false;
  #autoOpened = false;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('click', this.#onDialogClick);
    this.refs.dialog.addEventListener('close', this.#onDialogClose);

    if (this.hasAttribute('data-manual')) return;

    const state = readState();
    if (state?.status === 'subscribed') return;
    if (state && Date.now() - state.at < Number(this.dataset.dismissDays || 14) * 864e5) return;

    this.#timer = window.setTimeout(this.#autoOpen, Number(this.dataset.delay || 8000));
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#timer);
    window.removeEventListener('scroll', this.#onScroll);
  }

  /** @param {Event} [event] */
  open(event) {
    const { dialog, email, formStep, successTitle } = this.refs;
    if (dialog.open) return;

    // Safari doesn't focus buttons on click, so prefer the clicked element over document.activeElement.
    const opener = event?.target instanceof HTMLElement ? event.target : document.activeElement;
    this.#returnFocus = opener instanceof HTMLElement ? opener : null;

    dialog.showModal();
    // On touch screens, focusing the field would pull the keyboard up over the sheet before it's read.
    const touch = window.matchMedia('(hover: none)').matches;
    const target = touch ? dialog : formStep.hidden ? successTitle : email;
    target?.focus({ preventScroll: true });
  }

  close() {
    this.refs.dialog.close();
  }

  clearError() {
    if (this.refs.error.textContent) this.#setError('');
  }

  /** @param {SubmitEvent} event */
  async submit(event) {
    event.preventDefault();
    if (this.#busy) return;

    const { email } = this.refs;
    const address = email.value.trim();
    if (!EMAIL_PATTERN.test(address)) {
      this.#setError(this.dataset.errorInvalid || 'Enter a valid email address.');
      email.focus();
      return;
    }

    this.#setError('');
    this.#setBusy(true);
    try {
      await this.#subscribe(address);
      this.#showSuccess(address);
    } catch (error) {
      console.error(error);
      this.#setError(this.dataset.errorFailed || 'Something went wrong. Please try again.');
    } finally {
      this.#setBusy(false);
    }
  }

  /** @param {string} email */
  async #subscribe(email) {
    const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.company ?? '')}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: SOURCE,
            profile: {
              data: { type: 'profile', attributes: { email, properties: { signup_page: location.pathname } } },
            },
          },
          relationships: { list: { data: { type: 'list', id: this.dataset.list } } },
        },
      }),
    });
    if (!response.ok) throw new Error(`Klaviyo subscription failed: ${response.status}`);
  }

  /** @param {string} email */
  #showSuccess(email) {
    const { formStep, successStep, successTitle, live } = this.refs;
    formStep.hidden = true;
    successStep.hidden = false;
    successTitle?.focus({ preventScroll: true });
    if (live) live.textContent = successTitle?.textContent ?? '';
    writeState('subscribed');

    // Let Klaviyo's onsite script tie this browser's activity (Viewed Product and so on) to the new profile.
    try {
      /** @type {any} */ (window).klaviyo?.identify?.({ email });
    } catch {}
  }

  /** @param {string} message */
  #setError(message) {
    this.refs.error.textContent = message;
    this.refs.email.setAttribute('aria-invalid', String(Boolean(message)));
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const { submit, submitLabel } = this.refs;
    this.#busy = busy;
    submit.disabled = busy;
    submit.toggleAttribute('data-busy', busy);
    submitLabel.textContent = busy ? this.dataset.submitting || 'Subscribing…' : this.dataset.submitLabel || 'Subscribe';
  }

  /** Open once, but not over the cookie banner or another open dialog: wait until they're gone. */
  #autoOpen = () => {
    window.clearTimeout(this.#timer);
    window.removeEventListener('scroll', this.#onScroll);
    if (this.#autoOpened) return;

    // assets/ari-cookie-banner.js flags Shopify's cookie banner and preferences window while they show.
    const root = document.documentElement;
    const busy =
      root.hasAttribute('data-ari-consent-banner') ||
      root.hasAttribute('data-ari-consent-prefs') ||
      [...document.querySelectorAll('dialog[open]')].some((dialog) => dialog !== this.refs.dialog);
    if (busy) {
      this.#timer = window.setTimeout(this.#autoOpen, RETRY_MS);
      return;
    }

    this.#autoOpened = true;
    this.open();
  };

  #onScroll = () => {
    const root = document.documentElement;
    const percent = (root.scrollTop / Math.max(1, root.scrollHeight - root.clientHeight)) * 100;
    if (percent >= Number(this.dataset.scroll || 40)) this.#autoOpen();
  };

  /** Backdrop clicks land on the <dialog> itself, outside its box. */
  #onDialogClick = (/** @type {MouseEvent} */ event) => {
    const { dialog } = this.refs;
    if (event.target !== dialog) return;

    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) dialog.close();
  };

  /** Runs for the close button, Esc and backdrop clicks alike. */
  #onDialogClose = () => {
    if (this.refs.successStep.hidden && readState()?.status !== 'subscribed') writeState('dismissed');

    const target = this.#returnFocus;
    this.#returnFocus = null;
    if (target?.isConnected && !target.closest('[hidden]')) target.focus({ preventScroll: true });
  };
}

/** @returns {{ status: string, at: number } | null} */
function readState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

/** @param {'dismissed' | 'subscribed'} status */
function writeState(status) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ status, at: Date.now() }));
  } catch {}
}

if (!customElements.get('ari-ride-report')) {
  customElements.define('ari-ride-report', AriRideReport);
}
